import React, { useState } from 'react';
import { PencilSquareIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

// One screening question as a card — the Requirement Results look (colour bar, number badge, skill tag, Q box,
// "What to Listen For" + "Red Flags" side by side). No follow-up box. With onSave / onDelete / onMove it gets the
// edit / delete / move icons (the HM review page); without them it is read-only (the recruiter screen).
// q = {skill, question, expected_answer, red_flags}

export const skillCounts = (questions) => {
  const counts = new Map();
  (questions || []).forEach((q) => { const k = q.skill || 'General'; counts.set(k, (counts.get(k) || 0) + 1); });
  return [...counts.entries()];
};

export function SkillChips({ questions }) {
  return (
    <div className="flex flex-wrap gap-2.5" data-testid="skill-chips">
      {skillCounts(questions).map(([skill, n]) => (
        <span key={skill} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold border bg-blue-100 text-blue-800 border-blue-200">
          💻 {skill} · {n}
        </span>
      ))}
    </div>
  );
}

const Lines = ({ text, dot }) => (
  <>
    {String(text || '—').split('\n').filter((l) => l.trim()).map((line) => (
      <div key={line} className="flex items-start gap-2">
        {/^[•-]/.test(line.trim()) ? (<><span className={`${dot} mt-1 flex-shrink-0`}>●</span><span>{line.replace(/^\s*[•-]\s*/, '')}</span></>) : <span>{line}</span>}
      </div>
    ))}
  </>
);

const iconBtn = 'w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-30';

function Editor({ q, onSave, onCancel, busy }) {
  const [d, setD] = useState({ skill: q.skill || '', question: q.question || '', expected_answer: q.expected_answer || '', red_flags: q.red_flags || '' });
  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.value }));
  const field = 'w-full px-3 py-2 border rounded-lg text-sm';
  return (
    <div className="px-6 pb-5 space-y-3" data-testid="question-editor">
      <label className="block text-xs font-bold text-gray-600">Skill
        <input aria-label="Skill" value={d.skill} onChange={set('skill')} className={`${field} mt-1 border-gray-200 font-normal`} />
      </label>
      <label className="block text-xs font-bold text-gray-600">Question
        <textarea aria-label="Question" rows={2} value={d.question} onChange={set('question')} className={`${field} mt-1 border-brand-blue font-semibold text-gray-900`} />
      </label>
      <div className="grid md:grid-cols-2 gap-4">
        <label className="block text-xs font-bold text-green-800">What to Listen For (expected answer)
          <textarea aria-label="Expected answer" rows={4} value={d.expected_answer} onChange={set('expected_answer')} className={`${field} mt-1 border-green-200 bg-green-50 font-normal text-gray-800`} />
        </label>
        <label className="block text-xs font-bold text-red-800">Red Flags
          <textarea aria-label="Red flags" rows={4} value={d.red_flags} onChange={set('red_flags')} className={`${field} mt-1 border-red-200 bg-red-50 font-normal text-gray-800`} />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold">Cancel</button>
        <button onClick={() => onSave({ ...q, ...d, question: d.question.trim() })} disabled={busy || !d.question.trim()} data-testid="question-save"
          className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-bold disabled:opacity-50">Save</button>
      </div>
    </div>
  );
}

function ScreeningQuestionCard({ q, index, total = 0, editing = false, busy = false, onEdit, onSave, onCancel, onDelete, onMove }) {
  const [confirm, setConfirm] = useState(false);
  const editable = !!onSave;
  return (
    <div className="group relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden" data-testid="question">
      <div className="h-1.5 bg-blue-500" />
      <div className="px-6 pt-5 pb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center text-white font-bold text-sm shadow-md">{index + 1}</div>
          <span className="px-3 py-1 rounded-full text-xs font-bold border bg-blue-100 text-blue-800 border-blue-200">
            💻 Technical Skills – {q.skill || 'General'}
          </span>
        </div>
        {editable && !editing && (
          <div className="flex gap-1">
            <button onClick={onEdit} title="Edit question" aria-label={`Edit question ${index + 1}`} className={`${iconBtn} text-brand-blue hover:bg-blue-50`}>
              <PencilSquareIcon className="w-5 h-5" />
            </button>
            <button onClick={() => setConfirm(true)} title="Delete question" aria-label={`Delete question ${index + 1}`} className={`${iconBtn} text-red-600 hover:bg-red-50`}>
              <TrashIcon className="w-5 h-5" />
            </button>
            <button onClick={() => onMove(-1)} disabled={busy || index === 0} title="Move up" aria-label={`Move question ${index + 1} up`} className={`${iconBtn} text-gray-600 hover:bg-gray-100`}>
              <ArrowUpIcon className="w-5 h-5" />
            </button>
            <button onClick={() => onMove(1)} disabled={busy || index === total - 1} title="Move down" aria-label={`Move question ${index + 1} down`} className={`${iconBtn} text-gray-600 hover:bg-gray-100`}>
              <ArrowDownIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
      {confirm && (
        <div className="mx-6 mb-3 flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800" data-testid="confirm-delete">
          Delete question {index + 1}?
          <span className="flex gap-2">
            <button onClick={() => setConfirm(false)} className="px-3 py-1 rounded bg-white border border-gray-200 text-gray-700">Keep</button>
            <button onClick={async () => { if (await onDelete()) setConfirm(false); }} disabled={busy} className="px-3 py-1 rounded bg-red-600 text-white font-bold">Delete</button>
          </span>
        </div>
      )}
      {editing ? <Editor q={q} onSave={onSave} onCancel={onCancel} busy={busy} /> : (
        <>
          <div className="px-6 pb-4">
            <div className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
              <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-brand-blue flex items-center justify-center mt-0.5">
                <span className="text-white font-bold text-xs">Q</span>
              </div>
              <p className="text-gray-900 font-semibold text-base leading-relaxed">{q.question}</p>
            </div>
          </div>
          <div className="px-6 pb-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl p-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100" data-testid="listen-for">
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-6 h-6 rounded-md bg-green-500 flex items-center justify-center"><span className="text-white text-xs">👂</span></div>
                  <h4 className="text-sm font-bold text-green-800">What to Listen For</h4>
                </div>
                <div className="text-sm text-green-700 leading-relaxed space-y-1.5"><Lines text={q.expected_answer} dot="text-green-400" /></div>
              </div>
              <div className="rounded-xl p-4 bg-gradient-to-br from-red-50 to-rose-50 border border-red-100" data-testid="red-flags">
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-6 h-6 rounded-md bg-red-500 flex items-center justify-center"><span className="text-white text-xs">🚩</span></div>
                  <h4 className="text-sm font-bold text-red-800">Red Flags</h4>
                </div>
                <div className="text-sm text-red-700 leading-relaxed space-y-1.5"><Lines text={q.red_flags} dot="text-red-400" /></div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ScreeningQuestionCard;
